<template>
  <div class="fu-drft" data-test="fu-drawer-footer">
    <span v-if="lockText" class="fu-drft__lock" data-test="fu-drawer-lock"><fu-icon name="lock" :size="13" />{{ lockText }}</span>

    <div class="fu-drft__actions">
      <button
        v-if="showClassic"
        type="button"
        class="fu-drft__link fu-drft__link--quiet"
        title="Super Admin preview — reopen this patient in the classic follow-up drawer"
        data-test="fu-drawer-classic"
        @click="$emit('use-classic')"
      >Classic drawer</button>
      <button
        v-if="showProfile"
        type="button"
        class="fu-drft__link"
        data-test="fu-drawer-profile"
        @click="$emit('open-profile')"
      >Patient profile <fu-icon name="arrowR" :size="15" /></button>
      <button type="button" class="fu-drft__cancel" :disabled="saving" data-test="fu-drawer-cancel" @click="$emit('cancel')">{{ formOn ? 'Cancel' : 'Close' }}</button>
      <button
        v-if="formOn"
        type="button"
        class="fu-drft__save"
        :disabled="saveDisabled || saving"
        data-test="fu-drawer-save"
        @click="$emit('save')"
      >
        <fu-icon :name="saving ? 'spin' : saveIcon" :size="15" :class="{ 'fu-drft__spin': saving }" />{{ saveLabel }}
      </button>
    </div>
  </div>
</template>

<script>
/**
 * DrawerFooter — the lock note, "Patient profile →", the Super-Admin-only "Classic drawer" link
 * (reopens today's CreateFollowup.vue — the container flips shouldUseNewFollowupsUi's override),
 * Cancel / Close and Save with the per-method label.
 *
 *   formOn        a method form is open (Save shows; the secondary button says Cancel, else Close)
 *   method        'phone' | 'sms' | 'email' | 'payment' | 'ai' — picks the Save icon
 *   saveLabel / saveDisabled   drawerMethods.saveButtonState(method, …)
 *   saving        a save is in flight (spinner; both buttons disabled)
 *   showProfile   false on Patient Profile itself
 *   showClassic   the user is a Super Admin (the container decides)
 *   lockText      "Teammates see Avery as in use while this panel is open." — '' hides it
 * Events: use-classic, open-profile, cancel, save. Presentational.
 */
import FuIcon from '../FuIcon.vue';

const SAVE_ICON = { phone: 'check', sms: 'send', email: 'send', payment: 'send', ai: 'bot' };

export default {
  name: 'DrawerFooter',
  components: { FuIcon },
  props: {
    formOn: { type: Boolean, default: false },
    method: { type: String, default: 'phone' },
    saveLabel: { type: String, default: 'Save attempt' },
    saveDisabled: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
    showProfile: { type: Boolean, default: true },
    showClassic: { type: Boolean, default: false },
    lockText: { type: String, default: '' },
  },
  computed: {
    saveIcon() {
      return SAVE_ICON[this.method] || 'check';
    },
  },
};
</script>

<style lang="scss" scoped>
@import './drawer';

.fu-drft {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 14px;
}

.fu-drft__lock {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: $fu-text-muted;
  .fu-icon { color: $fu-warning; }
}

.fu-drft__actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 10px;
}

.fu-drft__link {
  @include fu-link-btn;
  font-size: 13px;
  white-space: nowrap;
  margin-right: 4px;
}

.fu-drft__link--quiet { color: $fu-text-muted; font-weight: $fu-fw-medium; }

.fu-drft__cancel { @include fu-btn(secondary, md); }
.fu-drft__save { @include fu-btn(primary, md); }
.fu-drft__spin { @include fu-spin; }

.fu-dr--phone .fu-drft__lock { display: none; }
.fu-dr--phone .fu-drft__actions { width: 100%; }
.fu-dr--phone .fu-drft__cancel,
.fu-dr--phone .fu-drft__save { flex: 1 1 0; }
</style>
